import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CheckCircle2,
  Globe,
  Lock,
  PartyPopper,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import ROUTES from '@/routes/routes'
import { NEGATIVE, POSITIVE } from '../graphColors'
import type { WorldGraphEdge, WorldGraphState } from '../types'
import { BuildProgress, LastRunAlert, RunSummary, runFor } from './BuildStatus'
import { useConfirm } from './ConfirmDialog'
import { SETUP_LOCKED } from './ScenarioPhase'
import ChecksPanel from './ChecksPanel'
import GraphCanvas from './GraphCanvas'

interface Props {
  state: WorldGraphState
  anyRunning: boolean
  teamNames: Record<string, string>
  onGenerate: () => Promise<boolean>
  onFinalize: () => Promise<boolean>
  onSave: (edges: WorldGraphEdge[]) => Promise<boolean>
  onApprove: (edges: WorldGraphEdge[]) => Promise<boolean>
  onReopen: () => Promise<boolean>
}

/**
 * The causal links: which stock pushes which, how strongly, and after how many
 * rounds. They are the game's physics: a stock with no link to another cannot
 * affect it. Approving them finishes the world graph.
 */
export default function LinksPhase({
  state,
  anyRunning,
  teamNames,
  onGenerate,
  onFinalize,
  onSave,
  onApprove,
  onReopen,
}: Props) {
  const { nodes, edges } = state.graph
  const approved = state.links_approved
  const build = runFor(state.current_build, 'links', 'finalize')
  const running = build?.status === 'running'
  const lastRunFailed =
    build && (build.status === 'failed' || build.status === 'interrupted')
  const [draft, setDraft] = useState<WorldGraphEdge[]>(edges)
  const [busy, setBusy] = useState(false)
  const { confirm, dialog } = useConfirm()
  const [selectedNode, setSelectedNode] = useState<string | null>(null)
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null)

  useEffect(() => {
    setDraft(edges)
  }, [edges])

  const labels = useMemo(
    () => Object.fromEntries(nodes.map((n) => [n.id, n.label])),
    [nodes]
  )
  const dirty = JSON.stringify(draft) !== JSON.stringify(edges)
  const waiting = !state.stocks_approved
  const locked = approved || busy || anyRunning || waiting
  const shown = draft.filter((e) =>
    selectedEdge
      ? e.id === selectedEdge
      : selectedNode
        ? e.source === selectedNode || e.target === selectedNode
        : true
  )

  const run = async (action: () => Promise<boolean>) => {
    setBusy(true)
    const ok = await action()
    setBusy(false)
    return ok
  }

  const update = (id: string, changes: Partial<WorldGraphEdge>) =>
    setDraft((current) =>
      current.map((e) => (e.id === id ? { ...e, ...changes } : e))
    )

  const remove = (id: string) => {
    setDraft((current) => current.filter((e) => e.id !== id))
    if (selectedEdge === id) setSelectedEdge(null)
  }

  const regenerate = async () => {
    if (
      edges.length &&
      !(await confirm({
        title: 'Link the stocks again?',
        description: 'The new links replace these, including your edits.',
        confirmLabel: 'Link again',
        destructive: true,
      }))
    )
      return
    run(onGenerate)
  }

  const clearSelection = () => {
    setSelectedNode(null)
    setSelectedEdge(null)
  }

  const raising = draft.filter((e) => e.sign === '+').length

  return (
    <Card className='bg-white shadow-md'>
      {dialog}
      <CardHeader>
        <div className='flex items-start justify-between gap-4'>
          <div>
            <CardTitle className='text-lg text-dark-blue'>
              4. Causal links
            </CardTitle>
            <CardDescription className='max-w-2xl'>
              Which stock pushes which, how strongly, and after how many rounds.
              These are the game's physics: a decision only reaches a stock
              through these links. Approving them finishes the world graph.
            </CardDescription>
          </div>
          {approved && (
            <Badge
              variant='outline'
              className='gap-1 border-green-600/30 bg-green-50 text-green-700'
            >
              <Lock className='h-3.5 w-3.5' aria-hidden />
              Approved
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        {lastRunFailed && <LastRunAlert build={build} />}
        {waiting && (
          <p className='rounded-md bg-muted/60 p-3 text-sm text-muted-foreground'>
            The stocks are back under review. Approve them to carry on with the
            links; links to any stock you removed have gone with it.
          </p>
        )}

        {state.ready && (
          <div
            className='rounded-lg border border-green-600/30 bg-green-50 p-4 text-sm text-green-800'
            data-testid='world-graph-ready'
          >
            <div className='flex items-center gap-2 font-medium'>
              <PartyPopper className='h-4 w-4' aria-hidden />
              The world graph is ready: {nodes.length} stocks, {edges.length}{' '}
              links and {state.tick_rules.length} drift rules.
            </div>
            <ul className='mt-2 space-y-1 text-green-900'>
              {state.tick_rules.map((rule) => (
                <li key={rule.id}>
                  <span className='font-medium'>
                    {labels[rule.target] || rule.target}
                  </span>{' '}
                  moves {rule.delta > 0 ? '+' : ''}
                  {rule.delta} a round
                  {rule.start_round > 1
                    ? ` from round ${rule.start_round}`
                    : ''}
                  {rule.condition && rule.condition !== 'always' && (
                    <>
                      {' '}
                      while <code className='text-xs'>{rule.condition}</code>
                    </>
                  )}
                  {rule.rationale ? `: ${rule.rationale}` : ''}
                </li>
              ))}
            </ul>
            {build?.status === 'succeeded' && build.step === 'finalize' && (
              <div className='mt-2'>
                <RunSummary build={build} verb='Drift rules proposed' />
              </div>
            )}
            <Link
              to={ROUTES.PROFESSOR.ROOM_WORLD(state.room_id)}
              className='mt-3 inline-flex items-center gap-1 font-medium text-green-900 underline'
            >
              <Globe className='h-4 w-4' aria-hidden />
              Open the world view
            </Link>
          </div>
        )}

        {running && build ? (
          <BuildProgress build={build} />
        ) : !edges.length ? (
          <div className='rounded-lg border border-dashed border-gray-300 p-6'>
            <div className='flex items-center gap-2 font-medium text-dark-blue'>
              <Sparkles className='h-4 w-4' aria-hidden />
              Link the stocks
            </div>
            <p className='mt-1 text-sm text-muted-foreground'>
              The model reads the approved stocks and the baseline context and
              proposes how each one drives the others.
            </p>
            <Button
              className='mt-4 bg-dark-blue hover:bg-dark-blue-hovered'
              disabled={busy || anyRunning || waiting}
              onClick={() => run(onGenerate)}
            >
              <Sparkles className='h-4 w-4' aria-hidden />
              {lastRunFailed ? 'Start again' : 'Link the stocks'}
            </Button>
          </div>
        ) : (
          <>
            {approved && !state.ready && !running && (
              <Button
                variant='outline'
                disabled={busy || anyRunning}
                onClick={() => run(onFinalize)}
              >
                <Sparkles className='h-4 w-4' aria-hidden />
                Propose the drift rules again
              </Button>
            )}
            <div
              className='flex flex-wrap items-center gap-2 text-sm'
              data-testid='world-graph-link-summary'
            >
              <span className='font-medium text-dark-blue'>
                {draft.length} links
              </span>
              <span className='text-muted-foreground'>
                · {raising} raise · {draft.length - raising} lower
              </span>
            </div>
            {build?.status === 'succeeded' && build.step === 'links' && (
              <RunSummary build={build} verb='Linked' />
            )}
            <ChecksPanel
              errors={state.checks.errors}
              warnings={state.checks.warnings}
            />

            <GraphCanvas
              nodes={nodes}
              edges={draft}
              teamNames={teamNames}
              selectedNodeId={selectedNode}
              selectedEdgeId={selectedEdge}
              onSelectNode={(id) => {
                setSelectedEdge(null)
                setSelectedNode(id === selectedNode ? null : id)
              }}
              onSelectEdge={(id) => {
                setSelectedNode(null)
                setSelectedEdge(id === selectedEdge ? null : id)
              }}
            />

            {(selectedNode || selectedEdge) && (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                Showing{' '}
                {selectedEdge
                  ? 'the selected link'
                  : `links to and from ${labels[selectedNode!]}`}
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-7'
                  onClick={clearSelection}
                >
                  <X className='h-3.5 w-3.5' aria-hidden />
                  Show all
                </Button>
              </div>
            )}

            <ul className='space-y-2' data-testid='world-graph-links'>
              {shown.map((edge) => (
                <li
                  key={edge.id}
                  className='rounded-lg border p-3'
                  data-testid='world-graph-link'
                >
                  <div className='flex flex-wrap items-center gap-2 text-sm'>
                    <span className='font-medium'>
                      {labels[edge.source] || edge.source}
                    </span>
                    <select
                      aria-label={`Direction of ${edge.id}`}
                      value={edge.sign}
                      disabled={locked}
                      onChange={(e) =>
                        update(edge.id, {
                          sign: e.target.value as WorldGraphEdge['sign'],
                        })
                      }
                      className={cn(
                        'h-7 rounded-md border border-input bg-background px-1.5 text-sm font-medium'
                      )}
                      style={{
                        color: edge.sign === '+' ? POSITIVE : NEGATIVE,
                      }}
                    >
                      <option value='+'>raises</option>
                      <option value='-'>lowers</option>
                    </select>
                    <span className='font-medium'>
                      {labels[edge.target] || edge.target}
                    </span>
                    <span className='ml-auto flex items-center gap-1.5 text-xs text-muted-foreground'>
                      strength
                      <Input
                        aria-label={`Strength of ${edge.id}`}
                        type='number'
                        min={0}
                        max={1}
                        step={0.05}
                        value={edge.magnitude}
                        disabled={locked}
                        onChange={(e) =>
                          update(edge.id, { magnitude: Number(e.target.value) })
                        }
                        className='h-7 w-20'
                      />
                      after
                      <Input
                        aria-label={`Delay of ${edge.id}`}
                        type='number'
                        min={0}
                        step={1}
                        value={edge.lag}
                        disabled={locked}
                        onChange={(e) =>
                          update(edge.id, { lag: Number(e.target.value) })
                        }
                        className='h-7 w-16'
                      />
                      rounds
                      {!approved && (
                        <Button
                          type='button'
                          variant='ghost'
                          size='icon'
                          className='h-7 w-7 hover:text-red-700'
                          aria-label={`Remove link ${edge.id}`}
                          disabled={locked}
                          onClick={() => remove(edge.id)}
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      )}
                    </span>
                  </div>
                  <Textarea
                    aria-label={`Why ${edge.id}`}
                    value={edge.mechanism}
                    disabled={locked}
                    onChange={(e) =>
                      update(edge.id, { mechanism: e.target.value })
                    }
                    className='mt-2 min-h-[44px] text-sm'
                    rows={2}
                  />
                </li>
              ))}
            </ul>

            <div className='flex flex-wrap justify-end gap-2'>
              {approved && state.setup_locked ? (
                <p className='text-sm text-muted-foreground'>{SETUP_LOCKED}</p>
              ) : approved ? (
                <Button
                  variant='outline'
                  disabled={busy || anyRunning}
                  onClick={() => run(onReopen)}
                >
                  Re-open to edit
                </Button>
              ) : (
                <>
                  <Button
                    variant='outline'
                    disabled={locked}
                    onClick={regenerate}
                  >
                    <RefreshCw className='h-4 w-4' aria-hidden />
                    Link again
                  </Button>
                  <Button
                    variant='outline'
                    disabled={locked || !dirty}
                    onClick={() => run(() => onSave(draft))}
                  >
                    <Save className='h-4 w-4' aria-hidden />
                    Save changes
                  </Button>
                  <Button
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    disabled={locked || !draft.length}
                    onClick={() => run(() => onApprove(draft))}
                  >
                    <CheckCircle2 className='h-4 w-4' aria-hidden />
                    Approve links and finish
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
